import { useEffect, useMemo, useState } from "react";
import { Camera, Plus, Sparkles, CheckCircle2, ChevronLeft, ChevronRight, X, CalendarDays } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CMPortal } from "@/components/graphisme/CMPortal";
import { EventPubsPopup } from "@/components/graphisme/EventPublications";
import { SlotPicker, fmtD, fmtDur, TODAY } from "@/components/piloting/shared";

const HL_TYPES = { evenement: "Événement", offre: "Offre", campagne: "Campagne", prestation: "Prestation", saison: "Opération saisonnière", objectif: "Objectif" };
const TYPE_LABELS = { reel: "Reel", photo: "Photo", carrousel: "Carrousel", story: "Story", newsletter: "Newsletter", publicite: "Publicité", autre: "Autre" };
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const isoMonth = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

function Overlay({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl p-5 w-full max-w-lg max-h-[88vh] overflow-y-auto space-y-3" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold">{title}</h3>
          <button onClick={onClose} className="p-1 text-neutral-400 hover:text-neutral-700" data-testid={`${testid}-close`}><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* Réservation : rejoindre un shooting prévu OU réserver un créneau réellement disponible */
function BookingModal({ highlight, shootings, onClose, onDone }) {
  const future = shootings.filter((s) => s.status === "planifie" && s.date >= TODAY);
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); onDone(); onClose(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Overlay title={highlight ? `Shooting pour « ${highlight.name} »` : "Réserver un shooting"} onClose={onClose} testid="pe-booking-modal">
      {future.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-neutral-500 mb-1.5">Rejoindre un shooting déjà prévu</p>
          <div className="space-y-1.5">
            {future.map((s) => (
              <div key={s.id} className="flex items-center justify-between border border-neutral-200 rounded-lg px-3 py-2" data-testid={`pe-join-${s.id}`}>
                <p className="text-xs">{fmtD(s.date)}{s.time ? ` à ${s.time}` : ""}{s.location ? ` — ${s.location}` : ""}</p>
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy}
                  onClick={() => act(() => api.post(`/content-piloting/portal/shootings/${s.id}/attach`, { highlight_id: highlight?.id }), "Votre événement rejoint ce shooting")}
                  data-testid={`pe-join-btn-${s.id}`}>Ajouter à ce shooting</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div>
        <p className="text-[11px] font-semibold text-neutral-500 mb-1.5">Ou réserver un nouveau créneau (disponibilités réelles de l'agence)</p>
        <SlotPicker endpoint="/content-piloting/portal/shooting-slots" picked={slot} onPick={setSlot} testid="pe-slot-picker" />
        {slot && (
          <Button className="w-full bg-[#0B132B] text-white h-9 text-xs mt-2" disabled={busy}
            onClick={() => act(() => api.post("/content-piloting/portal/shootings/book", {
              highlight_id: highlight?.id || null, date: slot.date, time: slot.start, duration_min: 120,
            }), `Shooting confirmé le ${fmtD(slot.date)} à ${slot.start}`)}
            data-testid="pe-book-confirm">
            Confirmer le {fmtD(slot.date)} à {slot.start} ({fmtDur(120)})
          </Button>
        )}
      </div>
    </Overlay>
  );
}

export default function PilotageEspace() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("planning"); // planning | shootings | publications
  const [form, setForm] = useState(null); // "offre" | null
  const [f, setF] = useState({ name: "", type: "offre", event_date: "", offer: "" });
  const [base, setBase] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [span, setSpan] = useState(3); // 1 mois ou trimestre
  const [bookFor, setBookFor] = useState(undefined); // undefined = fermé, null = sans événement, objet = événement
  const [pubsFor, setPubsFor] = useState(null); // événement dont on affiche les publications

  const load = () => api.get("/content-piloting/portal/summary").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  const allHighlights = useMemo(() => (data?.months || []).flatMap((m) => m.highlights), [data]);
  const allItems = useMemo(() => (data?.months || []).flatMap((m) => m.items), [data]);

  if (data === null) return <div className="p-10 text-center"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin mx-auto" /></div>;
  if (data === false) return <p className="p-10 text-center text-sm text-neutral-400">Espace indisponible.</p>;

  const visibleMonths = Array.from({ length: span }, (_, i) => isoMonth(new Date(base.getFullYear(), base.getMonth() + i, 1)));
  const itemsOf = (hid) => allItems.filter((i) => i.highlight_id === hid);
  const shootingOf = (h) => (data.shootings || []).find((s) => s.id === h.shooting_id);
  const nextShooting = (data.shootings || []).find((s) => s.status === "planifie" && s.date >= TODAY);

  const submitOffre = async () => {
    if (!f.name.trim()) { toast.error("Nom requis"); return; }
    try {
      await api.post("/content-piloting/portal/highlights", { name: f.name, type: f.type, event_date: f.event_date, offer: f.offer, month: f.event_date ? f.event_date.slice(0, 7) : "" });
      toast.success("Proposition envoyée à votre agence");
      setForm(null); setF({ name: "", type: "offre", event_date: "", offer: "" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="pilotage-espace">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">Pilotage de votre communication</h1>
          <p className="text-xs text-neutral-400">Vos événements, shootings et publications — tout est synchronisé avec votre agence.</p>
        </div>
        <div className="flex-1" />
        <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => setForm(form ? null : "offre")} data-testid="pe-add-offre">
          <Sparkles className="w-3.5 h-3.5" /> Ajouter une offre / un événement
        </Button>
      </div>

      <div className="flex items-center gap-5 border-b border-neutral-200" data-testid="pe-tabs">
        {[["planning", "Mon planning", CalendarDays], ["shootings", "Mes shootings", Camera], ["publications", "Mes publications", CheckCircle2]].map(([id, l, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`pe-tab-${id}`}
            className={`pb-2 text-xs font-semibold border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${tab === id ? "border-[#0B132B] text-[#0B132B]" : "border-transparent text-neutral-400 hover:text-neutral-700"}`}>
            <Icon className="w-3.5 h-3.5" /> {l}
          </button>
        ))}
      </div>

      {form && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 space-y-3" data-testid="pe-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-[11px] text-neutral-500">Nom</label>
              <Input className="mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ex : Offre Saint-Valentin" data-testid="pe-name" /></div>
            <div><label className="text-[11px] text-neutral-500">Type</label>
              <select className="mt-1 w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="pe-type">
                <option value="offre">Offre commerciale</option><option value="evenement">Événement</option><option value="prestation">Prestation / sujet</option>
              </select></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-[11px] text-neutral-500">Date de l'événement</label><Input type="date" className="mt-1" value={f.event_date} onChange={(e) => setF({ ...f, event_date: e.target.value })} data-testid="pe-date" /></div>
            <div><label className="text-[11px] text-neutral-500">Offre</label><Input className="mt-1" value={f.offer} onChange={(e) => setF({ ...f, offer: e.target.value })} placeholder="-20 % sur…" data-testid="pe-offer" /></div>
          </div>
          <p className="text-[10px] text-neutral-400">L'événement sera placé automatiquement dans le mois de sa date.</p>
          <div className="flex gap-2">
            <Button size="sm" className="bg-[#0B132B] text-white h-8 text-xs" onClick={submitOffre} data-testid="pe-submit">Envoyer à l'agence</Button>
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setForm(null)} data-testid="pe-cancel">Annuler</Button>
          </div>
        </div>
      )}

      {/* ---------------- Vue 1 : Mon planning ---------------- */}
      {tab === "planning" && (
        <div className="space-y-3" data-testid="pe-planning">
          <div className="flex items-center gap-2">
            <button className="p-1.5 border border-neutral-200 rounded-lg hover:bg-neutral-50" onClick={() => setBase(new Date(base.getFullYear(), base.getMonth() - span, 1))} data-testid="pe-prev"><ChevronLeft className="w-4 h-4" /></button>
            <p className="text-sm font-semibold" data-testid="pe-period">
              {MONTHS[base.getMonth()]} {base.getFullYear()}
              {span === 3 && ` → ${MONTHS[new Date(base.getFullYear(), base.getMonth() + 2, 1).getMonth()]} ${new Date(base.getFullYear(), base.getMonth() + 2, 1).getFullYear()}`}
            </p>
            <button className="p-1.5 border border-neutral-200 rounded-lg hover:bg-neutral-50" onClick={() => setBase(new Date(base.getFullYear(), base.getMonth() + span, 1))} data-testid="pe-next"><ChevronRight className="w-4 h-4" /></button>
            <div className="flex-1" />
            <div className="flex rounded-lg border border-neutral-200 overflow-hidden">
              {[[1, "Mois"], [3, "Trimestre"]].map(([n, l]) => (
                <button key={n} onClick={() => setSpan(n)} data-testid={`pe-span-${n}`}
                  className={`px-3 py-1.5 text-xs font-medium ${span === n ? "bg-[#0B132B] text-white" : "text-neutral-400 hover:bg-neutral-50"}`}>{l}</button>
              ))}
            </div>
          </div>

          <div className={`grid gap-3 ${span === 3 ? "md:grid-cols-3" : ""}`}>
            {visibleMonths.map((mk) => {
              const [y, mo] = mk.split("-");
              const hls = allHighlights.filter((h) => (h.month || (h.event_date || "").slice(0, 7)) === mk);
              return (
                <div key={mk} className="bg-white border border-[#E2E8F0] rounded-xl p-3 space-y-2" data-testid={`pe-month-${mk}`}>
                  <p className="text-xs font-bold text-neutral-700">{MONTHS[+mo - 1]} {y}</p>
                  {hls.length === 0 && <p className="text-[11px] text-neutral-300">Rien de prévu</p>}
                  {hls.map((h) => {
                    const its = itemsOf(h.id);
                    const created = its.filter((i) => i.created).length;
                    const sh = shootingOf(h);
                    return (
                      <button key={h.id} onClick={() => setPubsFor(h.id)} data-testid={`pe-hl-${h.id}`}
                        className="w-full text-left border border-violet-100 bg-violet-50/40 rounded-xl p-3 hover:border-violet-300 hover:shadow-sm transition-all">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs font-semibold truncate">{h.name}</p>
                          <span className="text-[9px] text-violet-500 shrink-0">{HL_TYPES[h.type]}</span>
                        </div>
                        {h.event_date && <p className="text-[10px] text-neutral-400 mt-0.5">{fmtD(h.event_date)}</p>}
                        {h.offer && <p className="text-[10px] text-emerald-700 mt-0.5">Offre : {h.offer}</p>}
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <div className="h-1 flex-1 bg-violet-100 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500 rounded-full" style={{ width: its.length ? `${(created / its.length) * 100}%` : 0 }} />
                          </div>
                          <span className="text-[9px] text-neutral-400" data-testid={`pe-hl-progress-${h.id}`}>{created}/{its.length} contenus</span>
                        </div>
                        {its.filter((i) => i.publication_status === "a_valider").length > 0 && (
                          <button className="text-[10px] font-semibold text-amber-600 hover:text-amber-800 mt-1"
                            onClick={(e) => { e.stopPropagation(); setTab("publications"); }} data-testid={`pe-hl-avalider-${h.id}`}>
                            {its.filter((i) => i.publication_status === "a_valider").length} publication(s) à valider →
                          </button>
                        )}
                        <p className="text-[10px] mt-1 flex items-center gap-1 text-amber-600">
                          <Camera className="w-3 h-3" /> {sh ? `Shooting le ${fmtD(sh.date)}${sh.time ? ` à ${sh.time}` : ""}` : "Shooting à planifier"}
                        </p>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ---------------- Vue 2 : Mes shootings ---------------- */}
      {tab === "shootings" && (
        <div className="space-y-3" data-testid="pe-shootings-view">
          {nextShooting ? (
            <div className="bg-blue-50/60 border border-blue-100 rounded-2xl p-4" data-testid="pe-next-shooting">
              <p className="text-xs font-bold text-blue-800 flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" /> Prochain shooting</p>
              <p className="text-sm font-semibold text-blue-900 mt-1">{fmtD(nextShooting.date)}{nextShooting.time ? ` à ${nextShooting.time}` : ""}{nextShooting.location ? ` — ${nextShooting.location}` : ""} ({fmtDur(nextShooting.duration_min)})</p>
            </div>
          ) : (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 text-center" data-testid="pe-no-shooting">
              <p className="text-xs text-neutral-400">Aucun shooting programmé pour le moment.</p>
            </div>
          )}
          {data.allow_booking && (
            <Button size="sm" className="h-8 text-xs gap-1 bg-[#0B132B] text-white" onClick={() => setBookFor(null)} data-testid="pe-book-shooting">
              <Plus className="w-3.5 h-3.5" /> Planifier un shooting
            </Button>
          )}
          {(data.shootings || []).filter((s) => s.date >= TODAY || s.items.length > 0).map((s) => {
            const byHl = {};
            for (const i of s.items) {
              const hl = allHighlights.find((h) => h.id === i.highlight_id);
              const k = hl ? hl.name : "Autres contenus";
              (byHl[k] = byHl[k] || []).push(i);
            }
            return (
              <div key={s.id} className="bg-white border border-[#E2E8F0] rounded-2xl p-4 space-y-2" data-testid={`pe-shooting-${s.id}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold">{fmtD(s.date)}{s.time ? ` à ${s.time}` : ""}{s.location ? ` — ${s.location}` : ""}</p>
                  <span className="text-[10px] text-neutral-400">{s.items.filter((i) => i.shot).length}/{s.items.length} tournés</span>
                </div>
                {Object.entries(byHl).map(([hlName, list]) => (
                  <div key={hlName}>
                    <p className="text-[10px] font-bold uppercase tracking-wide text-violet-500">{hlName}</p>
                    {list.map((i) => (
                      <p key={i.id} className="text-xs text-neutral-600 flex items-center gap-1.5 py-0.5" data-testid={`pe-shooting-item-${i.id}`}>
                        {i.shot ? <CheckCircle2 className="w-3.5 h-3.5 text-teal-500" /> : <span className="w-3.5 h-3.5 rounded-full border border-neutral-300 inline-block" />}
                        {i.title} <span className="text-neutral-300">· {TYPE_LABELS[i.type]}</span>
                      </p>
                    ))}
                  </div>
                ))}
                {s.items.length === 0 && <p className="text-[11px] text-neutral-300">Les contenus à tourner seront ajoutés par votre agence.</p>}
              </div>
            );
          })}
        </div>
      )}

      {/* ---------------- Vue 3 : Mes publications (module existant réutilisé) ---------------- */}
      {tab === "publications" && (
        <div data-testid="pe-publications">
          <CMPortal />
        </div>
      )}

      {bookFor !== undefined && (
        <BookingModal highlight={bookFor} shootings={data.shootings || []} onClose={() => setBookFor(undefined)} onDone={load} />
      )}

      {pubsFor && (
        <EventPubsPopup event={allHighlights.find((h) => h.id === pubsFor)} items={itemsOf(pubsFor)}
          canBook={data.allow_booking} onBook={(h) => { setPubsFor(null); setBookFor(h); }}
          onClose={() => setPubsFor(null)} onChanged={load} />
      )}
    </div>
  );
}
